import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import {
    GovernanceV2Event,
    type GovernanceV2Page,
    portalTelemetry,
} from '@experiences/telemetry';
import {
    UiStack,
    UiText,
} from '@experiences/ui-common';
import { useRouteResolver } from '@experiences/util';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListSubheader from '@mui/material/ListSubheader';
import Skeleton from '@mui/material/Skeleton';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import Tokens from '@uipath/apollo-core';
import React, {
    useCallback,
    useMemo,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
    Link,
    matchPath,
    useLocation,
} from 'react-router';

import { PortalPrefix } from '../../../../common/constants/RouteNames';
import { routePaths } from '../../../../common/constants/routePaths';
import { isOrgAdminSelector } from '../../../../store/selectors';
import { useCodedDashboardsContext } from '../dashboards/hooks/useCodedDashboards';
import { useInfiniteScrollSentinel } from '../hooks/useInfiniteScrollSentinel';
import PinDashboardsButton from './PinDashboardsButton';

interface NavItem {
    id: string;
    labelId?: string;
    displayLabel?: string;
    to: string;
    navTarget: GovernanceV2Page;
    dashboardId?: string;
    dashboardName?: string;
}

interface NavSectionLink {
    to: string;
    navTarget: GovernanceV2Page;
}

interface NavSection {
    id: string;
    titleId: string;
    items: NavItem[];
    /** Past this many items the list scrolls in place instead of growing the nav. */
    scrollAfter?: number;
    /** Set when more rows exist than have been fetched; drives the scroll-triggered load. */
    hasMore?: boolean;
    onLoadMore?: () => void;
    showSkeletons?: boolean;
    errorLabelId?: string;
    headerAction?: React.ReactNode;
    link?: NavSectionLink;
}

const SKELETON_KEYS = [ 'skeleton-0', 'skeleton-1', 'skeleton-2' ] as const;
const DASHBOARD_SCROLL_AFTER = 5;

const useStyles = makeStyles(theme =>
    createStyles({
        nav: {
            position: 'relative',
            boxSizing: 'border-box',
            borderRight: `${Tokens.Stroke.StrokeS} solid ${theme.palette.semantic.colorBorderDeEmp}`,
            backgroundColor: theme.palette.semantic.colorBackground,
            height: '100%',
            transition: 'width 0.2s ease',
        },
        navExpanded: {
            width: '240px',
            minWidth: '240px',
        },
        navCollapsed: {
            width: Tokens.Spacing.SpacingXl,
            minWidth: Tokens.Spacing.SpacingXl,
        },
        navInner: {
            height: '100%',
            overflowY: 'auto',
        },
        collapseButton: {
            position: 'absolute',
            top: Tokens.Spacing.SpacingS,
            right: `-${Tokens.Spacing.SpacingS}`,
            zIndex: 100,
            width: Tokens.Spacing.SpacingL,
            height: Tokens.Spacing.SpacingL,
            padding: 0,
            border: `${Tokens.Stroke.StrokeS} solid ${theme.palette.semantic.colorBorderDeEmp}`,
            backgroundColor: theme.palette.semantic.colorBackground,
            color: theme.palette.semantic.colorForeground,
            boxShadow: Tokens.Shadow.ShadowCardElevation,
            '&:hover': { backgroundColor: theme.palette.action.hover },
        },
        sectionHeader: {
            cursor: 'pointer',
            padding: `${Tokens.Padding.PadM} ${Tokens.Spacing.SpacingBase}`,
            fontSize: '13px',
            fontWeight: 600,
            color: theme.palette.semantic.colorForeground,
            lineHeight: '32px',
        },
        sectionHeaderTitle: {
            flex: 1,
            fontWeight: 600,
        },
        sectionHeaderLink: {
            flex: 1,
            fontWeight: Tokens.FontFamily.FontWeightSemibold,
            color: theme.palette.semantic.colorForeground,
            textDecoration: 'none',
            '&:hover': { textDecoration: 'none' },
        },
        sectionHeaderLinkActive: { color: theme.palette.semantic.colorForegroundLink },
        sectionToggle: {
            padding: 0,
            marginLeft: Tokens.Spacing.SpacingXs,
            color: theme.palette.semantic.colorForeground,
        },
        headerActionSlot: {
            display: 'inline-flex',
            alignItems: 'center',
            '& button': { padding: 0 },
        },
        /*
         * Height of exactly `DASHBOARD_SCROLL_AFTER` rows, composed from the same tokens the rows
         * are built from (one line of text plus its vertical padding) so it tracks them rather than
         * being a standalone pixel guess.
         */
        scrollArea: {
            maxHeight: `calc((${Tokens.Spacing.SpacingM} + ${Tokens.Padding.PadM} + ${Tokens.Padding.PadM}) * ${DASHBOARD_SCROLL_AFTER})`,
            overflowY: 'auto',
            // Stops a scroll that reaches the end of this list from scrolling the page behind it.
            overscrollBehavior: 'contain',
        },
        navItem: {
            '&&': {
                paddingLeft: Tokens.Spacing.SpacingM,
                paddingTop: Tokens.Padding.PadM,
                paddingBottom: Tokens.Padding.PadM,
                borderLeft: `${Tokens.Stroke.StrokeM} solid transparent`,
                color: theme.palette.semantic.colorForeground,
                textDecoration: 'none',
            },
            '&&:hover': { backgroundColor: theme.palette.action.hover },
        },
        navItemActive: {
            '&&': {
                paddingLeft: Tokens.Spacing.SpacingM,
                paddingTop: Tokens.Padding.PadM,
                paddingBottom: Tokens.Padding.PadM,
                borderLeft: `${Tokens.Stroke.StrokeM} solid ${theme.palette.primary.main}`,
                backgroundColor: theme.palette.semantic.colorBackgroundSelected,
                color: theme.palette.semantic.colorForeground,
                textDecoration: 'none',
            },
        },
        navItemLabelActive: {
            fontWeight: 500,
            color: theme.palette.semantic.colorForegroundLink,
        },
        skeletonRow: {
            paddingLeft: Tokens.Spacing.SpacingM,
            paddingRight: Tokens.Spacing.SpacingBase,
            paddingTop: Tokens.Padding.PadM,
            paddingBottom: Tokens.Padding.PadM,
        },
        errorRow: {
            paddingLeft: Tokens.Spacing.SpacingM,
            paddingRight: Tokens.Spacing.SpacingBase,
            paddingTop: Tokens.Padding.PadM,
            paddingBottom: Tokens.Padding.PadM,
            color: theme.palette.semantic.colorErrorText,
        },
    }),
);

const POLICY_SECTION: NavSection = {
    id: 'policy',
    titleId: 'CLIENT_GOVERNANCE_V2_NAV_POLICY',
    items: [
        {
            id: 'policy-library',
            labelId: 'CLIENT_GOVERNANCE_V2_NAV_POLICY_LIBRARY',
            to: `${PortalPrefix}${routePaths.governancePolicyLibrary}`,
            navTarget: 'PolicyLibrary',
        },
        {
            id: 'policy-deployment',
            labelId: 'CLIENT_GOVERNANCE_V2_NAV_POLICY_DEPLOYMENT',
            to: `${PortalPrefix}${routePaths.governancePolicyDeployment}`,
            navTarget: 'PolicyDeployment',
        },
        {
            id: 'compliance-standards',
            labelId: 'CLIENT_GOVERNANCE_V2_NAV_POLICY_PACKS',
            to: `${PortalPrefix}${routePaths.governanceComplianceStandards}`,
            navTarget: 'PolicyPacks',
        },
    ],
};

// Tenant-scoped audit section; always visible (data scope enforced server-side).
const AUDIT_SECTION: NavSection = {
    id: 'audit',
    titleId: 'CLIENT_GOVERNANCE_V2_NAV_AUDIT',
    items: [
        {
            id: 'llm-audit',
            labelId: 'CLIENT_GOVERNANCE_V2_NAV_LLM_AUDIT',
            to: `${PortalPrefix}${routePaths.governanceLlmAudit}`,
            navTarget: 'LlmAudit',
        },
    ],
};

const OBSERVABILITY_SECTION: NavSection = {
    id: 'observability',
    titleId: 'CLIENT_GOVERNANCE_V2_NAV_OBSERVABILITY',
    items: [
        {
            id: 'traces',
            labelId: 'CLIENT_GOVERNANCE_V2_NAV_TRACES',
            to: `${PortalPrefix}${routePaths.governanceObservabilityTraces}`,
            navTarget: 'ObservabilityTraces',
        },
    ],
};

// In-portal demo dashboards (not coded-app iframes) shown above the pinned coded dashboards.
const STATIC_DASHBOARD_ITEMS: NavItem[] = [
    {
        id: 'compliance-violations',
        // Localized like its policy-section siblings (reuses the dashboard title key) rather
        // than a hardcoded English string — the coded dashboards below use displayLabel only
        // because their names are user-authored and not translatable.
        labelId: 'CLIENT_GOVERNANCE_V2_COMPLIANCE_VIOLATIONS_TITLE',
        to: `${PortalPrefix}${routePaths.governanceComplianceViolations}`,
        navTarget: 'GovernanceHealth',
    },
];

const useGovernanceV2ActiveNavItem = (): string | undefined => {
    const { pathname } = useLocation();
    if (matchPath(`${PortalPrefix}${routePaths.governanceLlmAudit}/*`, pathname)) {
        return 'llm-audit';
    }
    if (matchPath(`${PortalPrefix}${routePaths.governancePolicyLibrary}/*`, pathname)) {
        return 'policy-library';
    }
    if (matchPath(`${PortalPrefix}${routePaths.governancePolicyDeployment}/*`, pathname)) {
        return 'policy-deployment';
    }
    if (matchPath(`${PortalPrefix}${routePaths.governanceComplianceStandards}/*`, pathname)) {
        return 'compliance-standards';
    }
    if (matchPath(`${PortalPrefix}${routePaths.governanceComplianceViolations}/*`, pathname)) {
        return 'compliance-violations';
    }
    if (matchPath(`${PortalPrefix}${routePaths.governanceObservabilityTraces}/*`, pathname)) {
        return 'traces';
    }
    const dashMatch = matchPath(`${PortalPrefix}${routePaths.governanceDashboard}/*`, pathname);
    if (dashMatch?.params.dashboardId) {
        return `dashboard-${dashMatch.params.dashboardId}`;
    }
    if (matchPath(`${PortalPrefix}${routePaths.governanceNotifications}/*`, pathname)) {
        return 'notifications';
    }
    if (matchPath(`${PortalPrefix}${routePaths.governanceGettingStarted}/*`, pathname)) {
        return 'dashboards';
    }
    return undefined;
};

interface SectionProps {
    section: NavSection;
    activeId?: string;
}

const GovernanceV2NavSection: React.FC<SectionProps> = ({
    section, activeId,
}) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const getRoute = useRouteResolver();
    const [ open, setOpen ] = useState(true);
    const Icon = open ? ExpandLessIcon : ExpandMoreIcon;

    const toggle = useCallback(() => setOpen(prev => !prev), []);
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
        }
    }, [ toggle ]);

    const renderItem = (item: NavItem) => {
        const isActive = item.id === activeId;
        const label = item.displayLabel ?? (item.labelId ? translate({ id: item.labelId }) : '');
        return (
            <ListItemButton
                key={item.id}
                component={Link}
                to={getRoute(item.to)}
                onClick={() => portalTelemetry.trackEvent({
                    name: GovernanceV2Event.LeftNavClicked,
                    properties: {
                        NavTarget: item.navTarget,
                        DashboardId: item.dashboardId,
                        DashboardName: item.dashboardName,
                    },
                })}
                className={isActive ? classes.navItemActive : classes.navItem}
                aria-current={isActive ? 'page' : undefined}
                data-cy={`governance-v2-nav-${item.id}`}
            >
                <UiText className={isActive ? classes.navItemLabelActive : undefined}>
                    {label}
                </UiText>
            </ListItemButton>
        );
    };

    const renderBody = () => {
        // Static items (e.g. Compliance Violations) always render; the loading skeleton and
        // load-error row for the dynamic coded dashboards are appended around them rather than
        // replacing them — otherwise the static item would vanish whenever the coded-dashboard
        // fetch is pending (which never resolves locally) or fails.
        const itemNodes = section.items.map(renderItem);
        if (section.errorLabelId) {
            return (
                <>
                    {itemNodes}
                    <ListItem
                        disablePadding
                        className={classes.errorRow}
                        data-cy={`governance-v2-nav-section-${section.id}-error`}
                        role="alert"
                    >
                        <UiText>
                            {translate({ id: section.errorLabelId })}
                        </UiText>
                    </ListItem>
                </>
            );
        }
        if (section.showSkeletons) {
            return (
                <>
                    {itemNodes}
                    {SKELETON_KEYS.map(key => (
                        <ListItem
                            key={key}
                            disablePadding
                            className={classes.skeletonRow}
                            data-cy="governance-v2-nav-dashboard-skeleton"
                        >
                            <Skeleton
                                variant="text"
                                width="75%"
                                height={Tokens.Spacing.SpacingM}
                            />
                        </ListItem>
                    ))}
                </>
            );
        }
        return itemNodes;
    };

    const headerActionSlot = section.headerAction && (
        <span
            className={classes.headerActionSlot}
            role="presentation"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
        >
            {section.headerAction}
        </span>
    );

    const noop = useCallback(() => {}, []);
    const sentinelRef = useInfiniteScrollSentinel({
        hasMore: Boolean(section.hasMore),
        loadMore: section.onLoadMore ?? noop,
    });

    const { link } = section;
    const isHeaderActive = Boolean(link) && activeId === section.id;
    // Only scrolls once it would actually be long; a short list keeps its natural height.
    const isScrollable = Boolean(section.scrollAfter && section.items.length > section.scrollAfter);

    const header = link ? (
        <ListSubheader
            component="div"
            className={classes.sectionHeader}
            disableSticky
            data-cy={`governance-v2-nav-section-${section.id}`}
        >
            <UiStack
                direction="row"
                align="center"
                w="100%"
            >
                <Link
                    to={getRoute(link.to)}
                    className={`${classes.sectionHeaderLink}${isHeaderActive ? ` ${classes.sectionHeaderLinkActive}` : ''}`}
                    aria-current={isHeaderActive ? 'page' : undefined}
                    onClick={() => portalTelemetry.trackEvent({
                        name: GovernanceV2Event.LeftNavClicked,
                        properties: {
                            NavTarget: link.navTarget,
                            DashboardId: undefined,
                            DashboardName: undefined,
                        },
                    })}
                    data-cy={`governance-v2-nav-section-${section.id}-link`}
                >
                    {translate({ id: section.titleId })}
                </Link>
                {headerActionSlot}
                {section.items.length > 0 && (
                    <IconButton
                        size="small"
                        className={classes.sectionToggle}
                        onClick={toggle}
                        aria-expanded={open}
                        aria-label={translate({ id: open ? 'CLIENT_GOVERNANCE_V2_NAV_DASHBOARDS_COLLAPSE' : 'CLIENT_GOVERNANCE_V2_NAV_DASHBOARDS_EXPAND' })}
                        data-cy={`governance-v2-nav-section-${section.id}-toggle`}
                    >
                        <Icon fontSize="small" />
                    </IconButton>
                )}
            </UiStack>
        </ListSubheader>
    ) : (
        <ListSubheader
            component="div"
            role="button"
            tabIndex={0}
            aria-expanded={open}
            className={classes.sectionHeader}
            onClick={toggle}
            onKeyDown={handleKeyDown}
            disableSticky
            data-cy={`governance-v2-nav-section-${section.id}`}
        >
            <UiStack
                direction="row"
                align="center"
                w="100%"
            >
                <UiText className={classes.sectionHeaderTitle}>
                    {translate({ id: section.titleId })}
                </UiText>
                {headerActionSlot}
                <Icon fontSize="small" />
            </UiStack>
        </ListSubheader>
    );

    return (
        <>
            {header}
            <Collapse
                in={open}
                timeout="auto"
                unmountOnExit
            >
                <List
                    component="div"
                    disablePadding
                    className={isScrollable ? classes.scrollArea : undefined}
                >
                    {renderBody()}
                    {/* Pulls the next page once it scrolls into view. */}
                    {section.hasMore ? <div ref={sentinelRef} /> : null}
                </List>
            </Collapse>
        </>
    );
};

const GovernanceV2LeftNav: React.FC = () => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const activeId = useGovernanceV2ActiveNavItem();
    const isOrgAdmin = useSelector(isOrgAdminSelector);
    const isComplianceTrackerEnabled = useFeatureFlagValue(Features.EnableGovernanceComplianceTracker.name);
    // Compliance Violations is a per-org gated static dashboard; hidden when the flag is off.
    const isComplianceViolationsDashboardEnabled = useFeatureFlagValue(
        Features.EnableGovernanceV2ComplianceViolationsDashboard.name,
    );
    const {
        pinned, isLoading, error, hasMore, loadMore,
    } = useCodedDashboardsContext();

    const dashboardSection = useMemo<NavSection>(() => ({
        id: 'dashboards',
        titleId: 'CLIENT_GOVERNANCE_V2_NAV_DASHBOARDS',
        link: {
            to: `${PortalPrefix}${routePaths.governanceGettingStarted}`,
            navTarget: 'GettingStarted',
        },
        scrollAfter: DASHBOARD_SCROLL_AFTER,
        hasMore,
        onLoadMore: loadMore,
        showSkeletons: isLoading && pinned.length === 0,
        errorLabelId: error && pinned.length === 0 ? 'CLIENT_GOVERNANCE_V2_NAV_DASHBOARDS_LOAD_ERROR' : undefined,
        // Always offered: the picker is how a user with nothing pinned pins their first dashboard.
        headerAction: <PinDashboardsButton />,
        items: [
            ...(isComplianceViolationsDashboardEnabled && isOrgAdmin ? STATIC_DASHBOARD_ITEMS : []),
            // Already pinned-only, server-side — no client filter needed.
            ...pinned
                .map(d => ({
                    id: `dashboard-${d.id}`,
                    displayLabel: d.deploymentTitle,
                    // Folder key rides along — the route resolves the app URL from folder context.
                    to: `${PortalPrefix}${routePaths.governanceDashboard.replace(':dashboardId', d.id)}`
                        + `?folderKey=${encodeURIComponent(d.deploymentFolder.key)}`,
                    navTarget: 'Dashboard' as const,
                    dashboardId: d.id,
                    dashboardName: d.deploymentTitle,
                })),
        ],
    }), [ pinned, isLoading, error, hasMore, loadMore, isComplianceViolationsDashboardEnabled, isOrgAdmin ]);

    const violationsSection = useMemo<NavSection>(() => ({
        id: 'violations',
        titleId: 'CLIENT_GOVERNANCE_V2_NAV_VIOLATIONS',
        items: [
            ...(isComplianceTrackerEnabled ? [
                {
                    id: 'notifications',
                    labelId: 'CLIENT_GOVERNANCE_V2_NOTIFICATIONS_TITLE',
                    to: `${PortalPrefix}${routePaths.governanceNotifications}`,
                    navTarget: 'Notifications' as const,
                },
            ] : []),
        ],
    }), [ isComplianceTrackerEnabled ]);

    const sections = useMemo(
        () => {
            // AUDIT_SECTION is tenant-scoped (always visible); violations and observability stay
            // org-admin-only, matching the Policy section's gating.
            const violations = violationsSection.items.length > 0 ? [ violationsSection ] : [];
            return isOrgAdmin
                ? [ POLICY_SECTION, AUDIT_SECTION, dashboardSection, ...violations, OBSERVABILITY_SECTION ]
                : [ AUDIT_SECTION, dashboardSection ];
        },
        [ dashboardSection, violationsSection, isOrgAdmin ],
    );

    const [ collapsed, setCollapsed ] = useState(false);
    const toggleCollapsed = useCallback(() => setCollapsed(prev => !prev), []);

    return (
        <nav
            aria-label={translate({ id: 'CLIENT_GOVERNANCE' })}
            className={`${classes.nav} ${collapsed ? classes.navCollapsed : classes.navExpanded}`}
            data-cy="governance-v2-left-nav"
        >
            <IconButton
                size="small"
                className={classes.collapseButton}
                onClick={toggleCollapsed}
                aria-expanded={!collapsed}
                aria-label={translate({ id: collapsed ? 'CLIENT_GOVERNANCE_V2_NAV_EXPAND' : 'CLIENT_GOVERNANCE_V2_NAV_COLLAPSE' })}
                data-cy="governance-v2-nav-collapse-toggle"
            >
                {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>
            {!collapsed && (
                <div className={classes.navInner}>
                    <List disablePadding>
                        {sections.map(section => (
                            <GovernanceV2NavSection
                                key={section.id}
                                section={section}
                                activeId={activeId}
                            />
                        ))}
                    </List>
                </div>
            )}
        </nav>
    );
};

export default GovernanceV2LeftNav;
